import {
  GroupIds,
  NIMBUS_MACON_LINE,
  NIMBUS_MACON_MOCK_LINE,
  NIMBUS_ROCK_HILL_LINE,
  NIMBUS_ROCK_HILL_MOCK_LINE,
  NIMBUS_SCW_LINE,
  NIMBUS_SCW_MOCK_LINE,
} from "@/shared/group-configs/groups";
import { defineInboundSiteRegistry, type InboundSiteDefinition } from "@/shared/group-configs/inbound-site-registry";
import type { GptLiveV3LineAdmission } from "@/shared/voice-runtime";

export enum NimbusVoiceSiteId {
  Macon = "macon",
  RockHill = "rock_hill",
  Scw = "scw",
}

export type NimbusVoiceTimezone = "America/New_York" | "America/Phoenix";

export type NimbusVoiceSite = InboundSiteDefinition<GroupIds> &
  Readonly<{
    id: NimbusVoiceSiteId;
    displayName: string;
    timezone: NimbusVoiceTimezone;
    athenaDepartmentEhrIds: readonly string[];
    /**
     * Optional Valerie location allowlist for availability searches in a specific group.
     * Department-matched locations outside this list remain part of the site's referral and
     * appointment scope.
     */
    availabilityLocationIdsByGroup?: Readonly<Partial<Record<GroupIds, readonly string[]>>>;
  }>;

const NIMBUS_MACON_HEMLOCK_LOCATION_ID = "3ec8387b-6910-4b67-8988-0e1abea11220";

export const NIMBUS_VOICE_SITES = [
  {
    id: NimbusVoiceSiteId.Scw,
    displayName: "Sun City West (SCW)",
    timezone: "America/Phoenix",
    athenaDepartmentEhrIds: ["3"],
    inboundLines: [
      { groupId: GroupIds.NIMBUS_LIVE, calledNumber: NIMBUS_SCW_LINE },
      { groupId: GroupIds.NIMBUS_LIVE_SANDBOX, calledNumber: NIMBUS_SCW_MOCK_LINE },
    ],
  },
  {
    id: NimbusVoiceSiteId.RockHill,
    displayName: "Rock Hill",
    timezone: "America/New_York",
    athenaDepartmentEhrIds: ["1"],
    inboundLines: [
      { groupId: GroupIds.NIMBUS_LIVE, calledNumber: NIMBUS_ROCK_HILL_LINE },
      { groupId: GroupIds.NIMBUS_LIVE_SANDBOX, calledNumber: NIMBUS_ROCK_HILL_MOCK_LINE },
    ],
  },
  {
    id: NimbusVoiceSiteId.Macon,
    displayName: "Macon",
    timezone: "America/New_York",
    athenaDepartmentEhrIds: ["2"],
    availabilityLocationIdsByGroup: {
      [GroupIds.NIMBUS_LIVE]: [NIMBUS_MACON_HEMLOCK_LOCATION_ID],
    },
    inboundLines: [
      { groupId: GroupIds.NIMBUS_LIVE, calledNumber: NIMBUS_MACON_LINE },
      { groupId: GroupIds.NIMBUS_LIVE_SANDBOX, calledNumber: NIMBUS_MACON_MOCK_LINE },
    ],
  },
] as const satisfies readonly NimbusVoiceSite[];

const NIMBUS_VOICE_SITE_REGISTRY = defineInboundSiteRegistry(NIMBUS_VOICE_SITES);

/** Explicit line admission; experiments separately control traffic allocation. */
export const NIMBUS_GPT_LIVE_V3_ADMISSIONS: readonly GptLiveV3LineAdmission[] = [
  { groupId: GroupIds.NIMBUS_LIVE, calledNumber: NIMBUS_ROCK_HILL_LINE, allowProduction: true },
  ...[NIMBUS_MACON_MOCK_LINE, NIMBUS_ROCK_HILL_MOCK_LINE, NIMBUS_SCW_MOCK_LINE].map((calledNumber) => ({
    groupId: GroupIds.NIMBUS_LIVE_SANDBOX,
    calledNumber,
    allowProduction: false,
  })),
];

export function resolveNimbusVoiceSite(groupId: GroupIds, calledNumber: string): NimbusVoiceSite | null {
  return NIMBUS_VOICE_SITE_REGISTRY.resolve(groupId, calledNumber);
}

export function requireNimbusVoiceSite(groupId: GroupIds, calledNumber: string): NimbusVoiceSite {
  return NIMBUS_VOICE_SITE_REGISTRY.require(groupId, calledNumber);
}
